import React, { FC } from 'react';
import { NavLink } from 'react-router-dom';
import { MutationFunctionOptions } from '@apollo/client';
import { AccountProfileFragment, ProfileStatusEnum } from '@minecraft.graphql-operations';
import { useTranslation } from '@blocs.i18n';
import { UpdatesFeed } from '@blocs.braze';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import SCREEN_NAMES from '@consumer-talent.config/constants/screenNames';
import { StyledUpdateLink } from '@consumer-talent.components/core/NavLoggedIn/Styles';
import { HeadshotsAnnualModal } from '@consumer-talent.components/Headshots/HeadshotsAnnualModal';
import { HeadshotsBookedModal } from '@consumer-talent.components/Headshots/HeadshotsBookedModal';
import { HeadshotsFreeModal } from '@consumer-talent.components/Headshots/HeadshotsFreeModal';
import { HeadshotsMonthlyModal } from '@consumer-talent.components/Headshots/HeadshotsMonthlyModal';
import { HeadshotsTrialModal } from '@consumer-talent.components/Headshots/HeadshotsTrialModal';
import { useHeadshotEligibility } from '@consumer-talent.components/Headshots/useHeadshotEligibility';
import { useHeadshotModal } from '@consumer-talent.components/Headshots/useHeadshotModal';
import { useRouteHelper } from '@consumer-talent.utils/routeHelper';
import styled from 'styled-components';
import { DetailTextSmall, FlexDynamic, FlexRow } from '@minecraft.atoms';
import { useArtistContext } from '../ArtistContext';

// We have to override styles because of react-strap
// Ideally we'd be able to use <BodyTextSmall and links in here
const StyledNavigationWrapper = styled(FlexDynamic)`
  & a,
  & a:not([href]):not([tabindex]) {
    display: block;
    font-size: 0.875rem;
  }
`;

interface MainNavMenuProps {
  profile: AccountProfileFragment;
  setUpdatedProfileField: (options: MutationFunctionOptions) => void;
  alertsCount: number;
}

const MainNavMenu: FC<MainNavMenuProps> = ({ profile, setUpdatedProfileField, alertsCount }) => {
  const { t } = useTranslation();
  const { link } = useRouteHelper();
  const { isArtistLocked } = useArtistContext();
  const { creditsRemaining, showNavLink: showHeadshots, showSessionCount } = useHeadshotEligibility();
  const submissionTrends = useFeatureExperiment(FEATURE_FLAGS.SUBMISSION_TRENDS);
  const yourAuditions = useFeatureExperiment(FEATURE_FLAGS.AUDITIONS);
  const yourInsights = submissionTrends || yourAuditions;
  const {
    activeModal,
    closeModal,
    handleHeadshotsClick,
    trialEndDate,
    handleTrialUpgrade,
    sessionDateTime,
    nextSessionDate,
    handleUploadMedia,
    nextBookableDate,
    handleMonthlyUpgrade,
    handleFreeUpgrade,
  } = useHeadshotModal();

  return (
    <StyledNavigationWrapper
      direction="column"
      alignItems="flex-start"
      className="cn_mt-2"
      mdProps={{ direction: 'row', gap: '6', alignItems: 'center' }}
    >
      {profile.profileStatus.code === ProfileStatusEnum.Active && (
        <NavLink
          to={link('castingBillboard')}
          activeClassName="active"
          data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-casting-billboard`}
        >
          {t('common:mainNav.castingBillboard')}
        </NavLink>
      )}
      {!isArtistLocked && (
        <NavLink
          to={link('profile', profile.profileId.toString())}
          activeClassName="active"
          data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-your-profile`}
          onClick={() =>
            setUpdatedProfileField({
              variables: { updatedProfileField: null },
            })
          }
        >
          {t('common:mainNav.yourProfile')}
        </NavLink>
      )}
      {!isArtistLocked && profile.profileStatus.code === ProfileStatusEnum.Active && (
        <div className="cn_relative">
          <NavLink
            to={link('mediaBank')}
            activeClassName="active"
            data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-media-bank`}
          >
            {t('common:mainNav.yourMedia')}
          </NavLink>
        </div>
      )}
      {profile.profileStatus.code === ProfileStatusEnum.Active && (
        <NavLink
          to={link('yourAlerts')}
          activeClassName="active"
          data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-your-alerts`}
        >
          {t('common:mainNav.yourAlerts', { count: alertsCount })}
        </NavLink>
      )}
      {!isArtistLocked && profile.profileStatus.code === ProfileStatusEnum.Active && (
        <>
          {yourInsights && (
            <NavLink
              to="/your-insights"
              activeClassName="active"
              data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-your-insights`}
            >
              {t('common:mainNav.yourInsights')}
            </NavLink>
          )}
          {showHeadshots && (
            <NavLink
              to={link('headshots')}
              activeClassName="active"
              data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-headshots`}
              onClick={handleHeadshotsClick}
            >
              <FlexRow alignItems="center" gap="1">
                {t('common:mainNav.headshots')}
                {showSessionCount && (
                  <FlexRow
                    bgColor="primary"
                    borderRadius="full"
                    w="nav-badge"
                    h="nav-badge"
                    alignItems="center"
                    justifyContent="center"
                    shrink={0}
                    data-testid="headshots-session-count-badge"
                  >
                    <DetailTextSmall fontWeight="semibold" lineHeight="ultraCondensed" color="inverted">
                      {creditsRemaining}
                    </DetailTextSmall>
                  </FlexRow>
                )}
              </FlexRow>
            </NavLink>
          )}
          <div aria-label={t('common:mainNav.updates')}>
            <UpdatesFeed
              wrapperElement={StyledUpdateLink}
              additionalWrapperElProps={{
                'data-qa-id': `${SCREEN_NAMES.NAVIGATION_LINK}-braze-feed-updates`,
              }}
            />
          </div>
        </>
      )}
      {activeModal === 'trial' && (
        <HeadshotsTrialModal trialEndDate={trialEndDate} onUpgrade={handleTrialUpgrade} onClose={closeModal} />
      )}
      {activeModal === 'monthly' && (
        <HeadshotsMonthlyModal
          nextBookableDate={nextBookableDate}
          onUpgrade={handleMonthlyUpgrade}
          onClose={closeModal}
        />
      )}
      {activeModal === 'free' && <HeadshotsFreeModal onUpgrade={handleFreeUpgrade} onClose={closeModal} />}
      {activeModal === 'booked' && <HeadshotsBookedModal sessionDateTime={sessionDateTime} onClose={closeModal} />}
      {activeModal === 'annual' && (
        <HeadshotsAnnualModal
          nextSessionDate={nextSessionDate}
          onUploadMedia={handleUploadMedia}
          onClose={closeModal}
        />
      )}
    </StyledNavigationWrapper>
  );
};

export default MainNavMenu;
